import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Clock, Heart } from 'lucide-react';
import { organs as mockOrgans, diseases as mockDiseases } from '../mock';
import { contentApi } from '../lib/api';
import { stripHtml } from '../lib/richText';
import StickyCardStack from '../components/StickyCardStack';
import DiseaseCard from '../components/DiseaseCard';
import NavCategoryHub from '../components/NavCategoryHub';
import './Home.css';

const MARQUEE = ['MEDICAL KNOWLEDGE', 'CLINICAL LEARNING', 'SMARTER MEDICINE', 'BETTER DECISIONS'];

const REASONS = [
  { title: 'Clear clinical information', text: 'Complex medical concepts transformed into structured, understandable information.', to: '/diseases' },
  { title: 'Visual learning', text: 'Anatomy, diseases and clinical concepts presented visually for faster understanding.', to: '/anatomy' },
  { title: 'Bedside calculators', text: 'BMI, ASCVD, Creatinine Clearance and more — with the formula and its limits shown.', to: '/calculators' },
  { title: 'Built for everyday medicine', text: 'Practical resources designed for students, doctors and healthcare professionals.', to: '/quiz' },
];

const DRUGS = [
  { name: 'Amoxicillin', kind: 'Antibiotic' },
  { name: 'Metformin', kind: 'Antidiabetic' },
];

// Scroll-reveal for elements marked .hm-reveal inside the given root.
function useReveal(rootRef, deps = []) {
  useEffect(() => {
    const root = rootRef.current;
    if (!root) return undefined;

    const items = root.querySelectorAll('.hm-reveal:not(.is-visible)');
    if (!('IntersectionObserver' in window)) {
      items.forEach((el) => el.classList.add('is-visible'));
      return undefined;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        });
      },
      { threshold: 0.12 }
    );

    items.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
}

// Pointer parallax on the hero orb, its orbits and the floating cards (optimized with rAF).
function useHeroParallax(coreRef, orbitsRef, cardsRef) {
  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return undefined;
    if ('ontouchstart' in window || navigator.maxTouchPoints > 0) return undefined;

    let rafId = null;
    let mouseX = 0;
    let mouseY = 0;

    const updateParallax = () => {
      const x = mouseX;
      const y = mouseY;

      if (coreRef.current) {
        coreRef.current.style.transform =
          `translateY(${-y * 15}px) rotateX(${y * -8}deg) rotateY(${x * 12}deg)`;
      }

      orbitsRef.current.forEach((orbit, i) => {
        if (!orbit) return;
        const strength = (i + 1) * 8;
        orbit.style.marginLeft = `${x * strength}px`;
        orbit.style.marginTop = `${y * strength}px`;
      });

      cardsRef.current.forEach((card, i) => {
        if (!card) return;
        const amount = i === 0 ? 15 : -12;
        card.style.transform = `translate(${x * amount}px, ${y * amount}px)`;
      });

      rafId = null;
    };

    const onMove = (event) => {
      if (window.innerWidth < 1000) return;
      mouseX = event.clientX / window.innerWidth - 0.5;
      mouseY = event.clientY / window.innerHeight - 0.5;

      if (!rafId) {
        rafId = window.requestAnimationFrame(updateParallax);
      }
    };

    window.addEventListener('mousemove', onMove, { passive: true });
    return () => {
      window.removeEventListener('mousemove', onMove);
      if (rafId) window.cancelAnimationFrame(rafId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}

// The opening screen: headline and orb alone, filling the viewport. The hero
// is pinned (see .hm-hero-pin in Home.css), so scrolling does not move it.
//
// --intro is a switch, not a scrubber: 0 on arrival, 1 the moment the page has
// been scrolled at all. One flick of the wheel is enough, and the CSS
// transitions play the zoom-out through to the end on their own rather than
// tracking how far the wheel has travelled. Scrolling back to the very top
// flips it home again.
//
// Everything outside Home falls back to 1 (the resting state) because the
// property simply isn't set there.
const INTRO_TRIGGER_PX = 4; // ignore sub-pixel jitter / scroll restoration noise

// The opening zoom is a welcome, not a transition. It belongs to a fresh
// arrival on the site; coming back to Home from another page should land on
// the settled hero, not replay the whole thing. A reload starts it over,
// which is the point.
let introSeen = false;

function useIntroScroll() {
  useEffect(() => {
    const root = document.documentElement;

    if (introSeen || window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      root.style.setProperty('--intro', '1');
      return () => root.style.removeProperty('--intro');
    }
    introSeen = true;

    let ticking = false;
    let settled = null;

    const update = () => {
      const next = window.scrollY > INTRO_TRIGGER_PX;
      // Only touch the property on an actual flip, so the transition is never
      // restarted mid-flight by an unrelated scroll event.
      if (next === settled) return;
      settled = next;
      root.style.setProperty('--intro', next ? '1' : '0');
    };

    const request = () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(() => {
        update();
        ticking = false;
      });
    };

    update();
    window.addEventListener('scroll', request, { passive: true });

    return () => {
      window.removeEventListener('scroll', request);
      // Leaving Home: drop the property so every other page renders at rest.
      root.style.removeProperty('--intro');
    };
  }, []);
}

// A fixed readout reads as a screenshot. A slow random walk around a resting
// rate, pulled gently back towards it so it never wanders off, looks like a
// monitor without turning into a distraction.
const RESTING_BPM = 72;

function useHeartRate() {
  const [bpm, setBpm] = useState(RESTING_BPM);

  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return undefined;

    let timer;
    const tick = () => {
      setBpm((current) => {
        const pull = (RESTING_BPM - current) * 0.25;
        const next = current + pull + (Math.random() * 4.4 - 2.2);
        return Math.min(84, Math.max(61, Math.round(next)));
      });
      // Uneven spacing keeps it from looking like a counter on a timer.
      timer = setTimeout(tick, 1500 + Math.random() * 1600);
    };

    timer = setTimeout(tick, 1800);
    return () => clearTimeout(timer);
  }, []);

  return bpm;
}

export default function Home() {
  useIntroScroll();
  const bpm = useHeartRate();
  const [organs, setOrgans] = useState(mockOrgans);
  const [diseases, setDiseases] = useState(mockDiseases);

  const rootRef = useRef(null);
  const coreRef = useRef(null);
  const orbitsRef = useRef([]);
  const cardsRef = useRef([]);

  useEffect(() => {
    contentApi.listOrgans().then((d) => d?.length && setOrgans(d)).catch(() => {});
    contentApi.listDiseases().then((d) => d?.length && setDiseases(d)).catch(() => {});
  }, []);

  // Re-run once live content lands so late-mounted cards still reveal.
  useReveal(rootRef, [organs, diseases]);
  useHeroParallax(coreRef, orbitsRef, cardsRef);

  const diseaseSystems = [...new Set(diseases.map((d) => d.category).filter(Boolean))].slice(0, 4);

  return (
    <div className="dp-home" ref={rootRef}>

      {/* HERO — the pin wrapper is taller than the screen by exactly the
          intro's scroll distance, so the sticky hero inside it holds still for
          the first scroll (zoom + navbar only) and releases on the next one. */}
      <div className="hm-hero-pin">
      <section className="hm-hero">
        <div className="hm-hero-content">
          <div className="hm-eyebrow">
            <span className="hm-pulse-dot" />
            MEDICAL EDUCATION PLATFORM
          </div>

          <h1>
            MEDICINE,<br />
            <span>SIMPLIFIED.</span>
          </h1>

          <div className="hm-hero-buttons">
            <Link to="/anatomy" className="hm-btn-primary">
              Start Learning <span aria-hidden="true">↗</span>
            </Link>
            <Link to="/diseases" className="hm-btn-secondary">
              Browse Diseases <span aria-hidden="true">↓</span>
            </Link>
          </div>

          <div className="hm-trust">
            <div className="hm-avatars">
              <span>DR</span>
              <span>MB</span>
              <span>MD</span>
            </div>
            <div>
              <strong>Built for healthcare</strong>
              <small>Doctors · Students · Professionals</small>
            </div>
          </div>
        </div>

        {/* 3D medical visual */}
        <div className="hm-visual" data-testid="hero-visual">
          <div className="hm-glow" />

          {[0, 1, 2].map((i) => (
            <div
              key={i}
              ref={(el) => { orbitsRef.current[i] = el; }}
              className={`hm-orbit hm-orbit-${i + 1}`}
            />
          ))}

          <div className="hm-core-wrap" ref={coreRef}>
            <div className="hm-core">
              <div className="hm-core-inner">
                <div className="hm-heart">
                  <svg viewBox="0 0 100 100" aria-hidden="true">
                    <path d="M50 82 C45 77 18 58 18 36 C18 20 29 12 41 12 C48 12 54 17 58 24 C62 17 68 12 76 12 C88 12 96 21 96 35 C96 56 72 76 50 82Z" />
                  </svg>
                </div>

                <div className="hm-ecg">
                  <svg viewBox="0 0 600 120" preserveAspectRatio="none" aria-hidden="true">
                    <polyline points="0,65 80,65 110,65 130,20 150,100 175,65 270,65 300,65 330,35 345,65 360,65 390,65 410,15 430,105 455,65 600,65" />
                  </svg>
                </div>

                <div className="hm-core-label">
                  <span>HEART RATE</span>
                  <strong>{bpm}</strong>
                  <small>BPM</small>
                </div>
              </div>
            </div>
          </div>

          <Link
            to="/anatomy/heart"
            className="hm-float-card hm-card-top"
            ref={(el) => { cardsRef.current[0] = el; }}
          >
            <div className="hm-card-icon"><Heart className="w-4 h-4" /></div>
            <div>
              <span>Explore anatomy</span>
              <strong>The Heart</strong>
            </div>
            <div className="hm-status-dot" />
          </Link>

          <Link
            to="/quiz"
            className="hm-float-card hm-card-bottom"
            ref={(el) => { cardsRef.current[1] = el; }}
          >
            <div className="hm-mini-chart">
              {[35, 55, 45, 75, 60, 90].map((h, i) => (
                <span key={i} style={{ height: `${h}%` }} />
              ))}
            </div>
            <div>
              <span>Track progress</span>
              <strong>Daily quiz</strong>
            </div>
          </Link>

          <div className="hm-float-number">
            <span>01</span>
            <small>LEARN</small>
          </div>
        </div>

        {/* Fades out as the intro resolves (opacity is driven by --intro).
            Without it nothing signals that the page continues below. */}
        <div className="hm-intro-cue" aria-hidden="true">
          <span>SCROLL</span>
          <i />
        </div>
      </section>
      </div>

      {/* MARQUEE */}
      <section className="hm-marquee-section" aria-hidden="true">
        <div className="hm-marquee">
          {[...MARQUEE, ...MARQUEE].map((text, i) => (
            <React.Fragment key={`${text}-${i}`}>
              <span>{text}</span>
              <i>✦</i>
            </React.Fragment>
          ))}
        </div>
      </section>

      {/* QUICK CATEGORY HUB (MATCHING USER WIREFRAME PHOTO) */}
      <NavCategoryHub />

      {/* PLATFORM */}
      <section className="hm-section">
        <div className="hm-section-heading hm-reveal">
          <div className="hm-section-number">01 / PLATFORM</div>
          <h2>
            Everything you need <span>to understand medicine.</span>
          </h2>
          <p>
            One environment for anatomy, diseases, drugs, clinical tools and
            medical learning.
          </p>
        </div>

        <div className="hm-feature-grid">
          <Link to="/anatomy" className="hm-feature-card hm-large-card hm-reveal">
            <div className="hm-feature-top">
              <span>01</span>
              <span>ANATOMY</span>
            </div>
            <div className="hm-anatomy-visual">
              <div className="hm-body-orb">
                <div className="hm-body-spine" />
                <div className="hm-body-head" />
                <div className="hm-body-heart" />
              </div>
            </div>
            <h3>Explore Anatomy</h3>
            <p>Understand human anatomy through immersive, labelled visual learning.</p>
            <span className="hm-feature-link">Explore anatomy ↗</span>
          </Link>

          <Link to="/diseases" className="hm-feature-card hm-reveal">
            <div className="hm-feature-top">
              <span>02</span>
              <span>DISEASES</span>
            </div>
            <div className="hm-disease-list">
              {diseaseSystems.map((system) => (
                <div key={system}>
                  <span className="hm-disease-dot" />
                  {system}
                </div>
              ))}
            </div>
            <h3>Understand Diseases</h3>
            <p>Clear clinical explanations from definition through to management.</p>
            <span className="hm-feature-link">Browse diseases ↗</span>
          </Link>

          <Link to="/pharma" className="hm-feature-card hm-reveal">
            <div className="hm-feature-top">
              <span>03</span>
              <span>DRUGS</span>
            </div>
            {DRUGS.map((drug) => (
              <div className="hm-drug-card" key={drug.name}>
                <div className="hm-drug-symbol">Rx</div>
                <div>
                  <strong>{drug.name}</strong>
                  <span>{drug.kind}</span>
                </div>
                <div className="hm-drug-arrow">↗</div>
              </div>
            ))}
            <h3>Know Your Drugs</h3>
            <p>Essential information for commonly used medications.</p>
            <span className="hm-feature-link">Explore drugs ↗</span>
          </Link>

          <Link to="/calculators" className="hm-feature-card hm-red-card hm-reveal">
            <div className="hm-feature-top">
              <span>04</span>
              <span>CLINICAL TOOLS</span>
            </div>
            <div className="hm-calc-symbol">
              <span>+</span>
              <span>×</span>
              <span>÷</span>
              <span>−</span>
            </div>
            <h3>Clinical Calculators</h3>
            <p>Fast, practical calculators for everyday clinical decision support.</p>
            <span className="hm-feature-link">View calculators ↗</span>
          </Link>
        </div>
      </section>

      {/* POPULAR ANATOMY */}
      <section className="hm-section" style={{ paddingTop: 0 }}>
        <div className="hm-section-heading hm-reveal">
          <div className="hm-section-number">02 / POPULAR</div>
          <h2>Anatomy <span>lessons.</span></h2>
          <p>Structured, labelled, and clinically correlated.</p>
        </div>
        <StickyCardStack items={organs.slice(0, 4)}>
          {(organ, i) => (
            <Link to={`/anatomy/${organ.slug}`} className="dp-stack-link">
              <div className="dp-stack-media">
                <img src={organ.image} alt={organ.name} loading="lazy" />
              </div>
              <div className="dp-stack-body">
                <div className="dp-stack-index">{String(i + 1).padStart(2, '0')} / ANATOMY</div>
                <div className="dp-stack-chips">
                  <span className="dp-chip dp-chip-red">{organ.system}</span>
                  <span className="dp-chip"><Clock className="w-3 h-3" /> {organ.readTime} min</span>
                </div>
                <h3 className="dp-stack-title">{organ.name}</h3>
                <p className="dp-stack-desc">{stripHtml(organ.desc)}</p>
                <span className="dp-stack-cta">
                  Start the lesson <ArrowRight className="w-4 h-4" />
                </span>
              </div>
            </Link>
          )}
        </StickyCardStack>
      </section>

      {/* STATEMENT */}
      <section className="hm-section hm-statement">
        <div className="hm-statement-line hm-reveal" />
        <div className="hm-statement-content hm-reveal">
          <span>THE FUTURE OF MEDICAL LEARNING</span>
          <h2>
            Learn faster.<br />
            Think clearer.<br />
            <em>Practice better.</em>
          </h2>
        </div>
        <div className="hm-statement-number" aria-hidden="true">03</div>
      </section>

      {/* WHY */}
      <section className="hm-section" style={{ paddingTop: 0 }}>
        <div className="hm-section-heading hm-reveal" style={{ marginBottom: 50 }}>
          <div className="hm-section-number">04 / WHY DR. PULSE</div>
          <h2>Designed around <span>how doctors think.</span></h2>
        </div>

        {REASONS.map((reason, i) => (
          <Link to={reason.to} className="hm-feature-row hm-reveal" key={reason.title}>
            <div className="hm-row-number">{String(i + 1).padStart(2, '0')}</div>
            <div className="hm-row-title"><h3>{reason.title}</h3></div>
            <div className="hm-row-description"><p>{reason.text}</p></div>
            <div className="hm-row-arrow" aria-hidden="true">↗</div>
          </Link>
        ))}
      </section>

      {/* MOST STUDIED DISEASES */}
      <section className="hm-section" style={{ paddingTop: 0 }}>
        <div className="hm-section-heading hm-reveal">
          <div className="hm-section-number">05 / MOST STUDIED</div>
          <h2>Diseases & <span>clinical conditions.</span></h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 mt-12">
          {diseases.slice(0, 6).map((d, i) => <DiseaseCard key={d.slug} disease={d} index={i} />)}
        </div>
      </section>

      {/* CTA */}
      <section className="hm-cta">
        <div className="hm-cta-orb" />
        <div className="hm-cta-content hm-reveal">
          <span>START LEARNING</span>
          <h2>
            Your medical<br />
            knowledge, <em>elevated.</em>
          </h2>
          <p>
            Save topics, track streaks, and get personal revision recommendations.
            No credit card required.
          </p>
          <div className="hm-cta-buttons">
            <Link to="/signup" className="hm-white-btn">
              Create free account <span aria-hidden="true">↗</span>
            </Link>
            <Link to="/anatomy" className="hm-btn-secondary" style={{ background: 'transparent', color: '#fff', borderColor: 'rgba(255,255,255,.35)' }}>
              Continue exploring
            </Link>
          </div>
        </div>
      </section>

    </div>
  );
}
